/**
 * Decides what the kusto `| render` auto-chart effect should do for a freshly
 * arrived result set. Split out as a pure function so the auto-vs-user-config
 * distinction (the crux of the stale-config bug) is unit-testable without a
 * live effect.
 *
 * The bug (Defect B): the effect stores its auto-applied config in the same
 * `chartConfigs[index]` slot it reads back as `activeChartConfig`, then guards
 * with `if (activeChartConfig) return` to respect USER configs — so the second
 * `| render` in a tab saw its own prior auto config and refused to re-open,
 * leaving a chart bound to columns that no longer exist.
 *
 * `autoAppliedConfig` is the exact ChartConfig object the effect last
 * auto-applied. Because the store preserves object identity and the chart
 * panel's onChange always emits a NEW object, an `activeChartConfig` that is
 * NOT reference-equal to `autoAppliedConfig` means the user authored/edited it.
 */
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import { renderHintToChartConfig, type RenderHint } from '../../charts/utils/renderHintToChartConfig';

export type RenderHintAction =
  /** No hint: drop render flags. `clearStoredConfig` is true only when the
   *  stored config is our own stale auto config (never a user's). */
  | { type: 'clear'; clearStoredConfig: boolean }
  /** Hint present but the user authored/edited the config: don't auto-open. */
  | { type: 'respect-user' }
  /** Hint present and mapped to a config: auto-apply it and open the chart. */
  | { type: 'apply'; config: ChartConfig }
  /** Hint present but no valid config could be built: leave things as-is. */
  | { type: 'skip' };

export function decideRenderHintAction(
  hint: RenderHint | undefined,
  columns: QueryColumn[],
  activeChartConfig: ChartConfig | undefined,
  autoAppliedConfig: ChartConfig | undefined,
): RenderHintAction {
  const userAuthoredConfig =
    activeChartConfig !== undefined && activeChartConfig !== autoAppliedConfig;

  if (!hint) {
    return { type: 'clear', clearStoredConfig: !userAuthoredConfig && activeChartConfig !== undefined };
  }
  if (userAuthoredConfig) {
    return { type: 'respect-user' };
  }
  const config = renderHintToChartConfig(hint, columns);
  if (!config) {
    return { type: 'skip' };
  }
  return { type: 'apply', config };
}
